/**
 *
 * IBM Confidential
 *
 * (C) Copyright IBM Corp. 2023, 2025
 *
 * The source code for this program is not published or otherwise
 * divested of its trade secrets, irrespective of what has been
 * deposited with the U. S. Copyright Office
 *
 * US Government Users Restricted Rights - Use, duplication or
 * disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 *
 **/
import { createAsyncThunk } from '@reduxjs/toolkit';
import { normalize } from 'normalizr';

import { ApiStatus } from '../../../../ts';
import { Collection, Collections } from '../../../../ts/collection';
import { AsyncAppThunk, CreateAsyncThunk } from '../../../../ts/state';
import { selectIsAccessUpdatesEnabled } from '../../../ia/store/features/selectors';
import { isCacheExpired } from '../../../ia/store/helper';
import { selectIsNewIa } from '../../../ia/store/instance/selectors';
import { addErrorMessage } from '../../../ia/store/messages/actions';
import {
  getActionSkillId,
  getAssistantId
} from '../../../ia/store/session/selectors';
import * as api from '../../api/collections';
import { NO_MATCHES_LABEL } from '../../util/actionUtil';
import { listActionsSuccess } from '../actions/actions';
import { actionsSchema } from '../actions/schema';
import { getCurrentActionId, getOrderedSteps } from '../actions/selectors';
import { selectAllCollections } from './selectors';

export const createCollection = createAsyncThunk<
  Collection,
  Partial<Collection>,
  CreateAsyncThunk
>('collection/create', async (params, { dispatch, getState }) => {
  try {
    const skillId = getActionSkillId(getState());
    // Use revised API for Access Control improvements if enabled & isNewIA
    // TODO when accessUpdates flag is removed, replace useNewIAUpdatedAccess with simpler isNewIA on call to create
    const assistantId = getAssistantId(getState());
    const isNewIA = selectIsNewIa(getState());
    const useNewIAUpdatedAccess =
      isNewIA && selectIsAccessUpdatesEnabled(getState());
    const response = await api.createCollection(
      skillId,
      useNewIAUpdatedAccess,
      assistantId,
      params
    );
    return response;
  } catch (e) {
    dispatch(addErrorMessage(e));
    throw e;
  }
});

export const updateCollection = createAsyncThunk<
  Collection,
  {
    options: { title: string; action_references: any[] };
    collection: string;
  },
  CreateAsyncThunk
>('collection/update', async (params, { dispatch, getState }) => {
  try {
    const skillId = getActionSkillId(getState());
    // Use revised API for Access Control improvements if enabled & isNewIA
    // TODO when accessUpdates flag is removed, replace useNewIAUpdatedAccess with simpler isNewIA on calls
    const assistantId = getAssistantId(getState());
    const isNewIA = selectIsNewIa(getState());
    const useNewIAUpdatedAccess =
      isNewIA && selectIsAccessUpdatesEnabled(getState());
    const response = await api.updateCollection(
      skillId,
      useNewIAUpdatedAccess,
      assistantId,
      params.collection,
      params.options
    );

    return response;
  } catch (e) {
    dispatch(addErrorMessage(e));
    throw e;
  }
});

export const fetchCollection = createAsyncThunk<
  Collection,
  { collection: string },
  CreateAsyncThunk
>('collection/get', async (params, { dispatch, getState }) => {
  try {
    const skillId = getActionSkillId(getState());
    // Use revised API for Access Control improvements if enabled & isNewIA
    // TODO when accessUpdates flag is removed, replace useNewIAUpdatedAccess with simpler isNewIA on calls
    const assistantId = getAssistantId(getState());
    const isNewIA = selectIsNewIa(getState());
    const useNewIAUpdatedAccess =
      isNewIA && selectIsAccessUpdatesEnabled(getState());
    const response = await api.getCollection(
      skillId,
      useNewIAUpdatedAccess,
      assistantId,
      params.collection
    );

    return response;
  } catch (e) {
    dispatch(addErrorMessage(e));
    throw e;
  }
});

export const setCurrentCollectionId = createAsyncThunk<
  string,
  string,
  CreateAsyncThunk
>('collection/set', (collectionId, { dispatch }) => {
  try {
    return collectionId;
  } catch (e) {
    dispatch(addErrorMessage(e));
    throw e;
  }
});

export const deleteCollection = createAsyncThunk<
  {},
  { collection: string; deleteActions: boolean },
  CreateAsyncThunk
>('collection/delete', async (params, { dispatch, getState }) => {
  try {
    const skillId = getActionSkillId(getState());
    // Use revised API for Access Control improvements if enabled & isNewIA
    // TODO when accessUpdates flag is removed, replace useNewIAUpdatedAccess with simpler isNewIA on calls
    const assistantId = getAssistantId(getState());
    const isNewIA = selectIsNewIa(getState());
    const useNewIAUpdatedAccess =
      isNewIA && selectIsAccessUpdatesEnabled(getState());
    const response = await api.deleteCollection(
      skillId,
      useNewIAUpdatedAccess,
      assistantId,
      params.collection,
      params.deleteActions
    );

    return response;
  } catch (e) {
    dispatch(addErrorMessage(e));
    throw e;
  }
});

export const listCollections = createAsyncThunk<
  Collections,
  void,
  CreateAsyncThunk
>('collection/list', async (__, { dispatch, getState }) => {
  try {
    const skillId = getActionSkillId(getState());
    // Use revised API for Access Control improvements if enabled & isNewIA
    // TODO when accessUpdates flag is removed, replace useNewIAUpdatedAccess with simpler isNewIA on calls
    const assistantId = getAssistantId(getState());
    const isNewIA = selectIsNewIa(getState());
    const useNewIAUpdatedAccess =
      isNewIA && selectIsAccessUpdatesEnabled(getState());
    const response = await api.listCollections(
      skillId,
      useNewIAUpdatedAccess,
      assistantId
    );
    const currentActionId = getCurrentActionId(getState());
    const allActions = response.actions.map(action => {
      if (
        action.action === 'anything_else' ||
        (action.condition &&
          'expression' in action.condition &&
          action?.condition?.expression === 'anything_else')
      ) {
        action.title = NO_MATCHES_LABEL;
      }
      if (action.action === currentActionId) {
        const orderedSteps = getOrderedSteps(getState());
        if (orderedSteps) {
          const stepsArray = Object.keys(orderedSteps).map(
            stepId => orderedSteps[stepId]
          );
          action.steps = stepsArray;
        }
      }
      return { ...action, id: action.action };
    });
    response?.collections?.map(collectionList => {
      collectionList.action_references.map(action => {
        const updatedAction = {
          ...action,
          id: action?.action
        };
        if (updatedAction.action === currentActionId) {
          const orderedSteps = getOrderedSteps(getState());
          if (orderedSteps) {
            const stepsArray = Object.keys(orderedSteps).map(
              stepId => orderedSteps[stepId]
            );
            updatedAction.steps = stepsArray;
          }
        }
        allActions.push(updatedAction);
      });
    });
    const normalizedResponse = normalize(allActions, actionsSchema);
    if (normalizedResponse.result && normalizedResponse.result.length === 0) {
      normalizedResponse.entities = { actions: {} };
    }
    dispatch(listActionsSuccess(skillId, normalizedResponse));

    return response;
  } catch (e) {
    dispatch(addErrorMessage(e));
    throw e;
  }
});

export const listCollectionsIfRequired = (): AsyncAppThunk<
  Promise<Collection[]>
> => {
  return async (dispatch, getState): Promise<Collection[]> => {
    const { listFetched, listStatus } = getState()?.collections;
    // Avoid requests if data is not stale
    if (!isCacheExpired(listFetched)) {
      return Promise.resolve(selectAllCollections(getState()));
    }
    if (
      listStatus !== ApiStatus.LOADING &&
      listStatus !== ApiStatus.SUCCEEDED
    ) {
      await dispatch(listCollections());
    }
    return selectAllCollections(getState());
  };
};
